"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, ArrowUpRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";
import { fmtCents } from "@/lib/format-price";
import { useMarketData } from "@/hooks/useMarketData";
import { formatUSD, timeAgo } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// Market Move Card — system event for big % price swings (pump/dump).
// Same slim event-card template as the actor rows, but the header swaps
// the avatar+actor for a trending-up/down chip + delta% + old→new price.
export default function MarketMoveRow({
  event,
  onVote,
}: {
  event: ActivityEvent;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const isPump = event.event_subtype === "pump";
  const priceChange = event.metadata?.price_change_percent || event.metadata?.price_change_pct || event.metadata?.priceChange;
  const oldPrice = event.metadata?.previous_price || event.metadata?.old_price || event.metadata?.oldPrice;
  const newPrice = event.metadata?.current_price || event.metadata?.new_price || event.metadata?.newPrice;

  const marketTitle = event.market_title;
  const marketHref = event.event_slug
    ? `/event/${event.event_slug}`
    : event.market_slug
      ? `/market/${event.market_slug}`
      : null;

  const { data: liveMarketData } = useMarketData(event.market_slug, {
    enabled: !!event.market_slug,
    source: 'polymarket',
  });
  const marketVolume = liveMarketData?.volume ?? null;
  const marketLiquidity = liveMarketData?.liquidity ?? null;
  const marketEndDate = liveMarketData?.endDate ?? null;
  const marketClosed = liveMarketData?.closed === true;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    router.push(`/activity/${event.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={CARD_BASE}
    >
      <div className="py-2.5 px-1">
        <div className="flex items-center gap-2.5">
          <span className={`flex items-center justify-center w-8 h-8 rounded-[5px] flex-shrink-0 ${
            isPump
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-red-500/10 text-red-600 dark:text-red-400'
          }`}>
            {isPump ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {isPump ? t("activity.move.pump") || "Pump" : t("activity.move.dump") || "Dump"}
                </span>
                {priceChange != null && (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
                    isPump
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-red-500/10 text-red-600 dark:text-red-400'
                  }`}>
                    {isPump ? "+" : ""}{Number(priceChange).toFixed(1)}%
                  </span>
                )}
                {(oldPrice != null || newPrice != null) && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {oldPrice != null && <span>{fmtCents(Number(oldPrice))}¢</span>}
                    {oldPrice != null && newPrice != null && <span className="mx-1">→</span>}
                    {newPrice != null && (
                      <span className={`font-semibold ${isPump ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                        {fmtCents(Number(newPrice))}¢
                      </span>
                    )}
                  </span>
                )}
              </div>
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {timeAgo(event.event_at, t)}
              </span>
            </div>
          </div>
        </div>

        {marketHref && marketTitle && (
          <Link
            href={marketHref}
            prefetch={false}
            onClick={(e) => e.stopPropagation()}
            className="block mt-2.5 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              {event.market_image && (
                <div className="w-8 h-8 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-white/[0.06]">
                  <CdnImage src={event.market_image} alt={marketTitle} width={32} height={32} sizes="32px" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {marketTitle}
                </h3>
                {(marketVolume != null || marketLiquidity != null || marketEndDate) && (() => {
                  const src = (event.metadata?.source as string) || 'polymarket';
                  const sourceLogo =
                    src === 'kalshi' ? '/logos/kalshi/k-icon.svg'
                    : src === 'hyperliquid' ? '/logos/hyperliquid/symbol-mint.svg'
                    : src === 'predict' ? ''
                    : '/logos/polymarket/icon-white.png';
                  const sourceLetter = src === 'predict' ? 'P' : '';
                  const sourceBg =
                    src === 'kalshi' ? 'bg-emerald-500/15'
                    : src === 'predict' ? 'bg-violet-500/15'
                    : src === 'hyperliquid' ? 'bg-cyan-500/15'
                    : 'bg-blue-500/15';
                  return (
                  <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-[4px] flex-shrink-0 ${sourceBg}`}>
                      {sourceLogo ? (
                        <img src={sourceLogo} alt={src} className="w-3.5 h-3.5 object-contain" />
                      ) : (
                        <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400">{sourceLetter}</span>
                      )}
                    </span>
                    {marketClosed && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">
                        {t("activity.badge.marketClosed")}
                      </span>
                    )}
                    {marketVolume != null && marketVolume > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketVolume)} vol</span>
                      </>
                    )}
                    {marketLiquidity != null && marketLiquidity > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketLiquidity)} liq</span>
                      </>
                    )}
                    {marketEndDate && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap">
                          {new Date(marketEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </>
                    )}
                  </div>
                  );
                })()}
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
          </Link>
        )}

        <div className="flex items-center gap-1.5 mt-2">
          <button
            onClick={() => onVote(event.id, 1, 'activity_event')}
            className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
              event.user_vote === 1 ? 'text-emerald-500 bg-emerald-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-emerald-500'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            <span>{event.reactions?.fire || event.likes || 0}</span>
          </button>
          <button
            onClick={() => onVote(event.id, -1, 'activity_event')}
            className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
              event.user_vote === -1 ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-red-500'
            }`}
          >
            <TrendingDown className="w-3.5 h-3.5" />
            <span>{event.reactions?.skull || event.dislikes || 0}</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}
